import React from "react";

import type { DataExportMetric } from "@features/data-export/types";

import { TertiaryButton } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";

import { SelectedMetric } from "./selected-metric";

interface Props {
  selectedMetrics: DataExportMetric[];
  editedMetricId: string | null;
  onChange(metrics: DataExportMetric[]): void;
}

export const SelectedMetrics = ({
  selectedMetrics,
  editedMetricId,
  onChange,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = selectedMetrics.findIndex(({ id }) => id === active.id);
      const newIndex = selectedMetrics.findIndex(({ id }) => id === over?.id);

      const newOrdering = arrayMove(selectedMetrics, oldIndex, newIndex);

      prettyLogEventPage({
        domain: "DataExport",
        action: "Other",
        additionalActionInfo: "Metrics reordered",
        data: {
          newMetrics: newOrdering,
          pageContext: { metrics: selectedMetrics },
        },
      });

      onChange(newOrdering);
    }
  };

  return (
    <div
      className="h-full border-l border-gray-100 overflow-hidden
                   grid grid-rows-[4rem_auto] bg-white z-40"
    >
      <div className="pl-7 pr-7 pt-5 flex items-center justify-between  font-semibold m-0 mb-2">
        <span className="text-lg">Metrics</span>
        <TertiaryButton onClick={() => onChange([])}>Clear All</TertiaryButton>
      </div>
      <div className="space-y-3 overflow-y-auto pl-7 pr-7 pb-5">
        {selectedMetrics.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-500">No metrics selected</p>
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[
            restrictToVerticalAxis,
            restrictToFirstScrollableAncestor,
          ]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={selectedMetrics.map(({ id }) => id)}>
            {selectedMetrics.map((e) => (
              <SelectedMetric
                key={e.id}
                metric={e}
                isEdited={e.id === editedMetricId}
                onRemove={() =>
                  onChange(selectedMetrics.filter((m) => m.id !== e.id))
                }
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
